/*
 * discount-modal.js
 *
 * Fires a one-time centered modal when the visitor lands on the
 * storefront via ?discount=CODE for a whitelisted code, with copy
 * sourced from the `discount_message` metaobject (server-side payload
 * in the snippet).
 *
 * Whitelist semantics: codes not configured as metaobject entries are
 * silent — Shopify still sets the discount cookie, we just don't show
 * a confirmation. Once-per-code-per-session via sessionStorage.
 *
 * Modal dismiss paths: × button, Esc key, backdrop click, "Shop now"
 * CTA. No auto-dismiss — modals require an explicit user action.
 */
(function () {
  'use strict';
  if (window.customElements && customElements.get('discount-modal')) return;

  var SESSION_KEY_PREFIX = 'fw:discount_modal:';
  var ANIM_MS = 220;
  // Suppress on these top-level paths — they own their own discount UI
  // or are private surfaces where a marketing modal doesn't belong.
  var SKIP_PATH_RE = /^\/(checkouts?|account|cart|gift_cards|policies)(\/|$)/;

  class DiscountModal extends HTMLElement {
    connectedCallback() {
      if (SKIP_PATH_RE.test(location.pathname)) return;

      var raw = new URLSearchParams(location.search).get('discount');
      if (!raw) return;
      var code = raw.trim().toUpperCase();
      if (!code) return;

      var seenKey = SESSION_KEY_PREFIX + code;
      try {
        if (sessionStorage.getItem(seenKey)) return;
      } catch (e) {
        // sessionStorage can be blocked (private mode, third-party context).
        // Better to over-show than to error out silently — proceed.
      }

      var messages = this._readPayload();
      var message = messages.find(function (m) {
        return m && m.code && m.code.trim().toUpperCase() === code;
      });
      if (!message) return; // whitelist — unknown codes silent

      // Display the original-cased code from the metaobject (so a URL
      // like ?discount=sweetspot still shows "SWEETSPOT" or whatever
      // marketing typed) while keeping our matching case-insensitive.
      this._displayCode = (message.code || code).trim().toUpperCase();

      this._panel = this.querySelector('[data-modal-panel]');
      this._subheadEl = this.querySelector('[data-modal-subhead]');
      this._subtextEl = this.querySelector('[data-modal-subtext]');
      this._closeBtn = this.querySelector('[data-modal-close]');
      this._backdropEl = this.querySelector('[data-modal-backdrop]');
      this._ctaBtn = this.querySelector('[data-modal-cta]');
      if (!this._panel || !this._subheadEl) return;

      this._render(message);
      this._show();

      try { sessionStorage.setItem(seenKey, '1'); } catch (e) {}

      try {
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'discount_modal_shown', { discount_code: this._displayCode });
        }
      } catch (e) {}
    }

    _readPayload() {
      var node = this.querySelector('[data-discount-modal-payload]');
      if (!node) return [];
      try {
        var data = JSON.parse(node.textContent);
        return Array.isArray(data) ? data : [];
      } catch (e) {
        console.warn('[discount-modal] payload parse failed', e);
        return [];
      }
    }

    _render(message) {
      // Subhead: "Your code <strong>SWEETSPOT</strong> gets you 10% off your
      // first subscription order."
      //
      // Built via DOM (not innerHTML) so the code/headline values can't be
      // exploited as HTML injection. We strip a trailing period from the
      // headline so we don't end up with double dots — the period is added
      // here as part of the template.
      var headline = (message.headline || '').replace(/\.\s*$/, '');

      this._subheadEl.textContent = '';
      this._subheadEl.appendChild(document.createTextNode('Your code '));
      var strong = document.createElement('strong');
      strong.textContent = this._displayCode;
      this._subheadEl.appendChild(strong);
      this._subheadEl.appendChild(document.createTextNode(' gets you ' + headline + '.'));

      if (message.subtext) {
        this._subtextEl.textContent = message.subtext;
        this._subtextEl.hidden = false;
      } else {
        this._subtextEl.hidden = true;
      }
    }

    _show() {
      var self = this;
      this.hidden = false;
      this._panel.hidden = false;

      // Lock background scroll while modal is open.
      this._prevBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      // Force reflow so the fade-in transition fires from initial state.
      void this._panel.offsetWidth;
      requestAnimationFrame(function () {
        self._panel.classList.add('is-visible');
      });

      this._closeHandler = function () { self._dismiss(); };
      this._backdropHandler = function () { self._dismiss(); };
      this._ctaHandler = function () { self._dismiss(); };

      this._closeBtn.addEventListener('click', this._closeHandler);
      this._backdropEl.addEventListener('click', this._backdropHandler);
      this._ctaBtn.addEventListener('click', this._ctaHandler);

      this._escHandler = function (e) {
        if (e.key === 'Escape') self._dismiss();
      };
      document.addEventListener('keydown', this._escHandler);

      // Move keyboard focus into the dialog so screen-readers announce
      // it and tab-stops stay scoped to the modal.
      try { this._closeBtn.focus({ preventScroll: true }); } catch (e) {}
    }

    _dismiss() {
      var self = this;
      if (this._dismissed) return;
      this._dismissed = true;

      if (this._escHandler) {
        document.removeEventListener('keydown', this._escHandler);
        this._escHandler = null;
      }
      if (this._closeHandler) this._closeBtn.removeEventListener('click', this._closeHandler);
      if (this._backdropHandler) this._backdropEl.removeEventListener('click', this._backdropHandler);
      if (this._ctaHandler) this._ctaBtn.removeEventListener('click', this._ctaHandler);

      this._panel.classList.remove('is-visible');
      this._panel.classList.add('is-leaving');

      setTimeout(function () {
        self._panel.hidden = true;
        self.hidden = true;
        self._panel.classList.remove('is-leaving');
        document.body.style.overflow = self._prevBodyOverflow || '';
      }, ANIM_MS);
    }
  }

  if (typeof window !== 'undefined' && window.customElements) {
    customElements.define('discount-modal', DiscountModal);
  }
})();
